import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { FaFilePdf } from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import PalletModel3D from '../components/PalletModel3D';
import { api } from '../api';

const NIVELES = [
  { key: 'superior', label: 'Tabla Superior', qtyLabel: 'tablas' },
  { key: 'medio',    label: 'Tabla Medio',    qtyLabel: 'tablas' },
  { key: 'tacon',    label: 'Tacón',          qtyLabel: 'tacones' },
  { key: 'inferior', label: 'Tabla Inferior', qtyLabel: 'tablas' },
];

const NIVELES_POLIN = [
  { key: 'superior', label: 'Tabla Superior',   qtyLabel: 'tablas' },
  { key: 'larguero', label: 'Larguero / Patín', qtyLabel: 'largueros' },
  { key: 'inferior', label: 'Tabla Inferior',   qtyLabel: 'tablas' },
];

const DIMS      = ['largo', 'ancho', 'alto'];
const DIM_LABEL = { largo: 'Largo', ancho: 'Ancho', alto: 'Alto' };

export default function FichaPublica() {
  const { id }           = useParams();
  const [searchParams]   = useSearchParams();
  const [ficha, setFicha]               = useState(null);
  const [error, setError]               = useState(null);
  const [snapshot, setSnapshot]         = useState(null);
  const [printTrigger, setPrintTrigger] = useState(0);
  const palletRef = useRef(null);

  useEffect(() => {
    if (!id) { setError('Link inválido.'); return; }
    api.getPublicFicha(id)
      .then(data => {
        setFicha(data);
        if (data.snapshot) setSnapshot(data.snapshot);
      })
      .catch(() => setError('Ficha no encontrada o link inválido.'));
  }, [id]);

  useEffect(() => {
    if (ficha && searchParams.get('print') === '1') {
      setTimeout(handlePDF, 1500);
    }
  }, [ficha]); // eslint-disable-line

  useEffect(() => {
    if (printTrigger > 0) window.print();
  }, [printTrigger]);

  const handlePDF = () => {
    const url = palletRef.current?.getSnapshot();
    if (url) {
      setSnapshot(url);
      setTimeout(() => setPrintTrigger(n => n + 1), 80);
    } else {
      setPrintTrigger(n => n + 1);
    }
  };

  if (error) return (
    <CenterWrap>
      <p style={{ color: 'var(--gray)', fontSize: '1rem' }}>{error}</p>
    </CenterWrap>
  );

  if (!ficha) return (
    <CenterWrap><p style={{ color: 'var(--gray)' }}>Cargando…</p></CenterWrap>
  );

  const {
    nombre,
    tipo         = 'tacon',
    form         = {},
    units        = {},
    tablasDet    = {},
    polinNiveles = {},
    polinUnits   = {},
    polinDet     = {},
    polinSeps    = { larguero: [] },
  } = ficha;

  const isPolin     = tipo === 'polin';
  const tipoLabel   = isPolin ? 'Polín / Rifle' : 'Tarima de Tacón';
  const niveles     = isPolin ? NIVELES_POLIN : NIVELES;
  const dims3d      = isPolin ? polinNiveles : form;
  const units3d     = isPolin ? polinUnits   : units;
  const tablasDet3d = isPolin ? polinDet     : tablasDet;

  const nivelData  = key => isPolin ? (polinNiveles[key] || {}) : (form[key] || {});
  const nivelUnits = key => isPolin ? (polinUnits[key]   || {}) : (units[key] || {});
  const nivelDet   = key => isPolin ? (polinDet[key]     || []) : (tablasDet[key] || []);
  const u          = (key, dim) => nivelUnits(key)[dim] || 'in';

  const shareUrl = `${window.location.origin}/fichas/ver/${id}`;

  return (
    <>
      <PageStyle />
      <Page>
        <PublicHeader className="no-print">
          <Logo src="/img/logo/grupojaycom_logo.png" alt="Grupo Jaycom" />
          <HeaderTitle>Ficha Técnica</HeaderTitle>
          <PDFBtn onClick={handlePDF}>
            <FaFilePdf size={14} /> Guardar PDF
          </PDFBtn>
        </PublicHeader>

        <Inner>
          <ModelSection className="no-print">
            <PalletModel3D
              ref={palletRef}
              tipo={tipo}
              dims={dims3d}
              units={units3d}
              tablasDet={tablasDet3d}
              seps={isPolin ? polinSeps : null}
              onSnapshot={setSnapshot}
            />
          </ModelSection>

          <PrintSheet>
            <PrintHeader>
              <PrintLogo src="/img/logo/grupojaycom_logo.png" alt="Grupo Jaycom" />
              <PrintHeaderRight>
                <PrintTitle>FICHA TÉCNICA</PrintTitle>
                <PrintSubtitle>{tipoLabel}</PrintSubtitle>
              </PrintHeaderRight>
              <PrintQRWrap>
                <QRCodeSVG value={shareUrl} size={80} bgColor="#ffffff" fgColor="#1F5D8C" level="M" />
                <PrintQRLabel>Ver ficha digital</PrintQRLabel>
              </PrintQRWrap>
            </PrintHeader>

            {snapshot && (
              <PrintModelWrap>
                <img src={snapshot} alt="Vista 3D de la tarima" style={{ width: '100%', height: 'auto', display: 'block' }} />
                <PrintModelCaption>Vista 3D — {tipoLabel}</PrintModelCaption>
              </PrintModelWrap>
            )}

            {nombre && <PrintNombre>{nombre}</PrintNombre>}
            <PrintMeta>
              <MetaRow><MetaKey>Cliente:</MetaKey><MetaVal>{form.cliente || '—'}</MetaVal></MetaRow>
              <MetaRow><MetaKey>Fecha:</MetaKey><MetaVal>{form.fecha || '—'}</MetaVal></MetaRow>
              <MetaRow><MetaKey>Carga estática:</MetaKey><MetaVal>{form.cargaEst ? `${form.cargaEst} kg` : '—'}</MetaVal></MetaRow>
              <MetaRow><MetaKey>Carga dinámica:</MetaKey><MetaVal>{form.cargaDin ? `${form.cargaDin} kg` : '—'}</MetaVal></MetaRow>
              {form.acabado && <MetaRow><MetaKey>Acabado:</MetaKey><MetaVal>{form.acabado}</MetaVal></MetaRow>}
            </PrintMeta>

            <PrintTableScroll>
              <PrintTable>
                <thead>
                  <tr>
                    <Th>Componente</Th>
                    <Th>Largo</Th>
                    <Th>Ancho</Th>
                    <Th>Alto</Th>
                    <Th>Cantidad</Th>
                  </tr>
                </thead>
                <tbody>
                  {niveles.map(({ key, label, qtyLabel }) => {
                    const nd      = nivelData(key);
                    const nu      = nivelUnits(key);
                    const det     = nivelDet(key);
                    const hasCustom = det.some(t => t.ancho !== '');
                    return (
                      <React.Fragment key={key}>
                        <tr>
                          <Td $bold data-label="Componente">{label}</Td>
                          {DIMS.map(dim => (
                            <Td key={dim} data-label={DIM_LABEL[dim]}>
                              {nd[dim] ? `${nd[dim]} ${nu[dim] || 'in'}` : '—'}
                            </Td>
                          ))}
                          <Td data-label="Cantidad">
                            {nd.qty ? `${nd.qty} ${qtyLabel}` : '—'}
                          </Td>
                        </tr>
                        {hasCustom && det.map((t, i) => (
                          <tr key={`${key}-${i}`} style={{ background: '#f7fbff' }}>
                            <Td style={{ paddingLeft: '1.5rem', color: '#1F5D8C', fontSize: '0.78rem' }} data-label="Componente">
                              ↳ Tabla #{i + 1}
                            </Td>
                            <Td style={{ color: '#555', fontSize: '0.78rem' }} data-label="Largo">—</Td>
                            <Td style={{ color: t.ancho ? '#1F5D8C' : '#999', fontWeight: t.ancho ? 700 : 400, fontSize: '0.78rem' }} data-label="Ancho">
                              {t.ancho ? `${t.ancho} ${t.unit}` : `${nd.ancho || '—'} ${nu.ancho || 'in'}`}
                              {!t.ancho && <span style={{ color: '#aaa', fontStyle: 'italic' }}> (hereda)</span>}
                            </Td>
                            <Td style={{ color: '#555', fontSize: '0.78rem' }} data-label="Alto">—</Td>
                            <Td style={{ color: '#555', fontSize: '0.78rem' }} data-label="Cantidad">1</Td>
                          </tr>
                        ))}
                        {isPolin && key === 'larguero' && polinNiveles.larguero?.resaque && (
                          <tr style={{ background: '#fff8f0' }}>
                            <Td style={{ paddingLeft: '1.5rem', color: '#7A5228', fontSize: '0.78rem' }} data-label="Componente">
                              ↳ Alto del corte (resaque)
                            </Td>
                            <Td style={{ color: '#999', fontSize: '0.78rem' }} data-label="Largo">—</Td>
                            <Td style={{ color: '#999', fontSize: '0.78rem' }} data-label="Ancho">—</Td>
                            <Td style={{ color: '#7A5228', fontWeight: 700, fontSize: '0.78rem' }} data-label="Alto">
                              {polinNiveles.larguero.resaque} {polinUnits.larguero?.resaque || 'in'}
                            </Td>
                            <Td style={{ color: '#999', fontSize: '0.78rem' }} data-label="Cantidad">2 cortes</Td>
                          </tr>
                        )}
                        {isPolin && key === 'larguero' && polinNiveles.larguero?.corte && (
                          <tr style={{ background: '#fff8f0' }}>
                            <Td style={{ paddingLeft: '1.5rem', color: '#7A5228', fontSize: '0.78rem' }} data-label="Componente">
                              ↳ Ancho de cada corte
                            </Td>
                            <Td style={{ color: '#999', fontSize: '0.78rem' }} data-label="Largo">—</Td>
                            <Td style={{ color: '#7A5228', fontWeight: 700, fontSize: '0.78rem' }} data-label="Ancho">
                              {polinNiveles.larguero.corte} {polinUnits.larguero?.corte || 'in'}
                            </Td>
                            <Td style={{ color: '#999', fontSize: '0.78rem' }} data-label="Alto">—</Td>
                            <Td style={{ color: '#999', fontSize: '0.78rem' }} data-label="Cantidad">—</Td>
                          </tr>
                        )}
                        {isPolin && key === 'larguero' && polinNiveles.larguero?.separacion && (
                          <tr style={{ background: '#fff8f0' }}>
                            <Td style={{ paddingLeft: '1.5rem', color: '#7A5228', fontSize: '0.78rem' }} data-label="Componente">
                              ↳ Separación entre largueros
                            </Td>
                            <Td style={{ color: '#999', fontSize: '0.78rem' }} data-label="Largo">—</Td>
                            <Td style={{ color: '#7A5228', fontWeight: 700, fontSize: '0.78rem' }} data-label="Ancho">
                              {polinNiveles.larguero.separacion} {polinUnits.larguero?.separacion || 'in'}
                            </Td>
                            <Td style={{ color: '#999', fontSize: '0.78rem' }} data-label="Alto">—</Td>
                            <Td style={{ color: '#999', fontSize: '0.78rem' }} data-label="Cantidad">—</Td>
                          </tr>
                        )}
                        {isPolin && key === 'larguero' && polinSeps.larguero?.some(s => s.sep !== '') &&
                          polinSeps.larguero.map((s, i) => s.sep !== '' && (
                            <tr key={`sep-${i}`} style={{ background: '#f7fbff' }}>
                              <Td style={{ paddingLeft: '2rem', color: '#1F5D8C', fontSize: '0.78rem' }} data-label="Componente">
                                ↳ Sep. larguero #{i+1}–#{i+2}
                              </Td>
                              <Td style={{ color: '#555', fontSize: '0.78rem' }} data-label="Largo">—</Td>
                              <Td style={{ color: '#1F5D8C', fontWeight: 700, fontSize: '0.78rem' }} data-label="Ancho">
                                {s.sep} {s.unit}
                              </Td>
                              <Td style={{ color: '#555', fontSize: '0.78rem' }} data-label="Alto">—</Td>
                              <Td style={{ color: '#555', fontSize: '0.78rem' }} data-label="Cantidad">1</Td>
                            </tr>
                          ))
                        }
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </PrintTable>
            </PrintTableScroll>

            <PrintFooter>
              <span>Grupo Jaycom — Fabricación de tarimas de madera</span>
              <span>contacto@grupojaycom.com</span>
            </PrintFooter>
          </PrintSheet>
        </Inner>
      </Page>
    </>
  );
}

/* ── Print CSS ── */
const PageStyle = createGlobalStyle`
  @page {
    size: A4 portrait;
    margin: 0;
  }
  @media print {
    .no-print { display: none !important; }
    body {
      background: white !important;
      margin: 0 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const CenterWrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Page = styled.div`
  min-height: 100vh;
  background: var(--mid);
  font-family: 'Inter', sans-serif;
  @media print {
    min-height: 0;
    background: white;
    padding: 0;
  }
`;

const PublicHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.85rem 2rem;
  background: white;
  border-bottom: 3px solid var(--blue);
  flex-wrap: wrap;
`;

const Logo = styled.img`height: 38px; width: auto;`;

const HeaderTitle = styled.h1`
  font-size: 1.15rem;
  color: var(--blue);
  font-family: 'Bebas Neue', sans-serif;
  letter-spacing: 0.06em;
  flex: 1;
`;

const PDFBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1.2rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  border-radius: 4px;
  background: var(--blue);
  color: white;
  transition: background 0.15s;
  &:hover { background: var(--blue-dk); }
`;

const Inner = styled.div`
  max-width: 900px;
  margin: 2rem auto;
  padding: 0 1.5rem 3rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  @media (max-width: 600px) { padding: 1rem 0.75rem 2rem; gap: 1rem; }
  @media print { margin: 0; padding: 0; gap: 0; max-width: 100%; }
`;

const ModelSection = styled.div`
  overflow: hidden;
  box-shadow: 0 2px 18px rgba(0,0,0,0.10);
`;

const PrintSheet = styled.div`
  background: white;
  padding: 3rem;
  border: 1px solid var(--border);
  border-top: 4px solid var(--blue);
  @media screen and (max-width: 600px) { padding: 1.25rem 1rem; }
  @media print { margin: 0; padding: 1.5cm 2cm 2cm; max-width: 100%; border: none; border-top: 4px solid #1F5D8C; }
`;

const PrintHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e5e5e5;
  break-inside: avoid;
  page-break-inside: avoid;
  @media (max-width: 500px) { flex-wrap: wrap; }
`;

const PrintLogo = styled.img`height: 70px; object-fit: contain;`;
const PrintHeaderRight = styled.div`text-align: right; flex: 1;`;

const PrintTitle = styled.h2`
  font-family: 'Bebas Neue', sans-serif;
  font-size: 2rem;
  color: #1F5D8C;
  letter-spacing: 0.06em;
`;

const PrintSubtitle = styled.p`
  font-size: 0.8rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #D0021B;
`;

const PrintQRWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;
`;

const PrintQRLabel = styled.span`
  font-size: 0.6rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #888;
  text-align: center;
`;

const PrintModelWrap = styled.div`
  margin-bottom: 1.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 3px;
  overflow: hidden;
  break-inside: avoid;
  page-break-inside: avoid;
`;

const PrintModelCaption = styled.p`
  font-size: 0.7rem;
  text-align: center;
  color: #888;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.35rem 0;
  background: #f9f9f9;
  border-top: 1px solid #e5e5e5;
`;

const PrintNombre = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #1F5D8C;
  margin-bottom: 0.75rem;
`;

const PrintMeta = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.4rem 2rem;
  margin-bottom: 2rem;
  break-inside: avoid;
  page-break-inside: avoid;
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const MetaRow = styled.div`display: flex; gap: 0.5rem; font-size: 0.88rem;`;
const MetaKey = styled.span`color: #555; min-width: 130px;`;
const MetaVal = styled.span`font-weight: 600; color: #1a1a1a;`;

const PrintTableScroll = styled.div`margin-bottom: 2.5rem;`;

const PrintTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  thead { display: table-header-group; }
  tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  @media (max-width: 540px) {
    thead { display: none; }
    tbody, tr, td { display: block; width: 100%; }
    tr { margin-bottom: 0.75rem; border: 1px solid #e5e5e5; border-radius: 4px; overflow: hidden; }
    td {
      display: flex; justify-content: space-between; align-items: center;
      padding: 0.5rem 0.85rem; border-bottom: 1px solid #f0f0f0; font-size: 0.84rem;
      &:last-child { border-bottom: none; }
      &::before {
        content: attr(data-label);
        font-size: 0.68rem; letter-spacing: 0.08em; text-transform: uppercase;
        color: #888; font-weight: 600; flex-shrink: 0; margin-right: 0.5rem;
      }
    }
  }
`;

const Th = styled.th`
  background: #1F5D8C; color: white; padding: 0.7rem 1rem;
  text-align: left; font-size: 0.72rem; letter-spacing: 0.1em;
  text-transform: uppercase; white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #e5e5e5;
  color: #1a1a1a;
  font-weight: ${({ $bold }) => $bold ? '700' : '400'};
  background: ${({ $bold }) => $bold ? '#EBF3FA' : 'transparent'};
`;

const PrintFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 1rem;
  border-top: 1px solid #e5e5e5;
  font-size: 0.75rem;
  color: #888;
  @media (max-width: 500px) { flex-direction: column; gap: 0.25rem; }
`;
